import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../components/ui/command";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, FileText, ChevronsUpDown, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const ClasificadorAmbulantes = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [items, setItems] = useState([]);
  const [formData, setFormData] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [filters, setFilters] = useState({ nombre: "", codigo: "", oficina: "", tasa: "", extranet: "", descuento: "" });
  const [codigosPresupuestarios, setCodigosPresupuestarios] = useState([]);
  const [oficinas, setOficinas] = useState([]);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try { const r = await axios.get(`${API}/api/ambulantes/clasificador`, { headers }); setItems(r.data); }
    catch { toast.error("Error al cargar clasificador de ambulantes"); }
  };
  const fetchCodigosPresupuestarios = async () => {
    try { const r = await axios.get(`${API}/api/definiciones/codigos-presupuestarios`, { headers }); setCodigosPresupuestarios(r.data); }
    catch { setCodigosPresupuestarios([]); }
  };
  const fetchOficinas = async () => {
    try { const r = await axios.get(`${API}/api/definiciones/oficinas`, { headers }); setOficinas(r.data); }
    catch { setOficinas([]); }
  };

  const handleSave = async () => {
    if (!formData.nombre) { toast.error("Nombre es obligatorio"); return; }
    try {
      if (editingItem) {
        await axios.put(`${API}/api/ambulantes/clasificador/${editingItem.id}`, formData, { headers });
        toast.success("Clasificador actualizado exitosamente");
      } else {
        await axios.post(`${API}/api/ambulantes/clasificador`, formData, { headers });
        toast.success("Clasificador creado exitosamente");
      }
      setDialogOpen(false); setFormData({}); setEditingItem(null); fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || "Error al guardar"); }
  };

  const openNew = () => {
    setEditingItem(null);
    setFormData({ extranet: false, descuento: false });
    fetchCodigosPresupuestarios(); fetchOficinas();
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setFormData({ ...item });
    fetchCodigosPresupuestarios(); fetchOficinas();
    setDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/api/ambulantes/clasificador/${itemToDelete.id}`, { headers });
      toast.success("Registro eliminado"); setDeleteDialogOpen(false); setItemToDelete(null); fetchData();
    } catch { toast.error("Error al eliminar"); }
  };

  const filtered = items.filter(item => {
    const f = filters;
    if (f.nombre && !(item.nombre || "").toLowerCase().includes(f.nombre.toLowerCase())) return false;
    if (f.codigo && !(item.codigo_presupuestario || "").toLowerCase().includes(f.codigo.toLowerCase())) return false;
    if (f.oficina && !(item.oficina || "").toLowerCase().includes(f.oficina.toLowerCase())) return false;
    if (f.tasa && !String(item.tasa || 0).includes(f.tasa)) return false;
    if (f.extranet && f.extranet !== "todos") { if ((f.extranet === "si") !== !!item.extranet) return false; }
    if (f.descuento && f.descuento !== "todos") { if ((f.descuento === "si") !== !!item.descuento) return false; }
    return true;
  });

  return (
    <div className="space-y-6" data-testid="clasificador-ambulantes-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Clasificador Ambulantes</h1>
          <p className="text-slate-500">Gestion del clasificador de vendedores ambulantes</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-sm" data-testid="registro-counter">
            <span className="font-bold text-blue-800">{filtered.length}</span>
            <span className="text-blue-600">de</span>
            <span className="font-bold text-blue-800">{items.length}</span>
            <span className="text-blue-600">registros</span>
          </div>
          <Button onClick={openNew} className="bg-orange-600 hover:bg-orange-700" data-testid="btn-nuevo-clasificador-amb">
            <Plus className="w-4 h-4 mr-2" /> Nuevo
          </Button>
        </div>
      </div>
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Nombre</TableHead>
              <TableHead>Cod. Presupuestario</TableHead>
              <TableHead>Oficina</TableHead>
              <TableHead className="text-right">Tasa</TableHead>
              <TableHead className="text-center">Descuento</TableHead>
              <TableHead className="text-center">Extranet</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
            <TableRow className="bg-slate-50/50 border-b">
              <TableHead className="px-3 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={filters.nombre} onChange={(e) => setFilters({...filters, nombre: e.target.value})} data-testid="filter-amb-nombre" /></TableHead>
              <TableHead className="px-3 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={filters.codigo} onChange={(e) => setFilters({...filters, codigo: e.target.value})} data-testid="filter-amb-codigo" /></TableHead>
              <TableHead className="px-3 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={filters.oficina} onChange={(e) => setFilters({...filters, oficina: e.target.value})} data-testid="filter-amb-oficina" /></TableHead>
              <TableHead className="px-3 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={filters.tasa} onChange={(e) => setFilters({...filters, tasa: e.target.value})} data-testid="filter-amb-tasa" /></TableHead>
              <TableHead className="px-3 py-1.5">
                <select className="h-7 w-full text-xs border rounded-md px-1 bg-white text-slate-700" value={filters.descuento} onChange={(e) => setFilters({...filters, descuento: e.target.value})} data-testid="filter-amb-descuento">
                  <option value="todos">Todos</option><option value="si">Si</option><option value="no">No</option>
                </select>
              </TableHead>
              <TableHead className="px-3 py-1.5">
                <select className="h-7 w-full text-xs border rounded-md px-1 bg-white text-slate-700" value={filters.extranet} onChange={(e) => setFilters({...filters, extranet: e.target.value})} data-testid="filter-amb-extranet">
                  <option value="todos">Todos</option><option value="si">Si</option><option value="no">No</option>
                </select>
              </TableHead>
              <TableHead className="px-3 py-1.5"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">No hay registros</TableCell></TableRow>
            ) : filtered.map((item) => (
              <TableRow key={item.id}>
                <TableCell className="text-sm font-medium">{item.nombre || "-"}</TableCell>
                <TableCell className="font-mono text-sm">{item.codigo_presupuestario || "-"}</TableCell>
                <TableCell className="text-sm">{item.oficina || "-"}</TableCell>
                <TableCell className="text-sm text-right">{(item.tasa || 0).toFixed(2)}</TableCell>
                <TableCell className="text-center">
                  {item.descuento ? (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700" title={`${item.porcentaje_descuento || 0}% | ${item.fecha_descuento_desde || ""} - ${item.fecha_descuento_hasta || ""}`}>
                      Si ({item.porcentaje_descuento || 0}%)
                    </span>
                  ) : (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">No</span>
                  )}
                </TableCell>
                <TableCell className="text-center">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${item.extranet ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}`}>
                    {item.extranet ? "Si" : "No"}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(item)} data-testid={`btn-edit-amb-${item.id}`}><Pencil className="w-4 h-4 text-blue-600" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => { setItemToDelete(item); setDeleteDialogOpen(true); }} data-testid={`btn-delete-amb-${item.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-orange-600" />
              {editingItem ? "Editar Clasificador" : "Nuevo Clasificador Ambulantes"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input value={formData.nombre || ""} onChange={(e) => setFormData({...formData, nombre: e.target.value})} placeholder="Ej: Venta de alimentos, Artesanias..." data-testid="form-nombre-amb" />
            </div>
            <div className="space-y-2">
              <Label>Codigo Presupuestario</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-between h-10 font-normal text-left" data-testid="form-codigo-presup-amb">
                    {formData.codigo_presupuestario
                      ? (() => { const cp = codigosPresupuestarios.find(c => c.codigo_presupuestario === formData.codigo_presupuestario); return cp ? `${cp.codigo_presupuestario} - ${cp.nombre_partida || ""}` : formData.codigo_presupuestario; })()
                      : <span className="text-slate-500">Seleccione codigo</span>}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[450px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Buscar codigo o partida..." />
                    <CommandList>
                      <CommandEmpty>No se encontraron codigos</CommandEmpty>
                      <CommandGroup>
                        {codigosPresupuestarios.map(cp => (
                          <CommandItem
                            key={cp.id || cp.codigo_presupuestario}
                            value={`${cp.codigo_presupuestario} ${cp.nombre_partida || ""}`}
                            onSelect={() => setFormData({...formData, codigo_presupuestario: cp.codigo_presupuestario})}
                          >
                            <Check className={`mr-2 h-4 w-4 ${formData.codigo_presupuestario === cp.codigo_presupuestario ? "opacity-100" : "opacity-0"}`} />
                            <span className="font-mono text-xs">{cp.codigo_presupuestario}</span>
                            <span className="ml-2 text-xs text-slate-500 truncate">{cp.nombre_partida || ""}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Oficina</Label>
                <Select value={formData.oficina || ""} onValueChange={(v) => setFormData({...formData, oficina: v})}>
                  <SelectTrigger data-testid="form-oficina-amb"><SelectValue placeholder="Seleccione oficina" /></SelectTrigger>
                  <SelectContent>
                    {oficinas.map((o) => <SelectItem key={o.id || o.nombre} value={o.nombre}>{o.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Tasa</Label>
                <Input type="number" step="0.01" value={formData.tasa ?? ""} onChange={(e) => setFormData({...formData, tasa: parseFloat(e.target.value) || 0})} placeholder="0.00" className="text-right" data-testid="form-tasa-amb" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Extranet</Label>
                <Select value={formData.extranet ? "si" : "no"} onValueChange={(v) => setFormData({...formData, extranet: v === "si"})}>
                  <SelectTrigger data-testid="form-extranet-amb"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="si">Si</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Descuento</Label>
                <Select value={formData.descuento ? "si" : "no"} onValueChange={(v) => {
                  const has = v === "si";
                  setFormData({...formData, descuento: has, ...(!has ? { porcentaje_descuento: 0, fecha_descuento_desde: "", fecha_descuento_hasta: "" } : {})});
                }}>
                  <SelectTrigger data-testid="form-descuento-amb"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="si">Si</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {formData.descuento && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                <div className="space-y-2">
                  <Label>Porcentaje de Descuento (%)</Label>
                  <Input type="number" step="0.01" value={formData.porcentaje_descuento || ""} onChange={(e) => setFormData({...formData, porcentaje_descuento: parseFloat(e.target.value) || 0})} placeholder="0.00" className="text-right max-w-[200px]" data-testid="form-porcentaje-desc-amb" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Fecha de Descuento - Desde</Label>
                    <Input type="date" value={formData.fecha_descuento_desde || ""} onChange={(e) => setFormData({...formData, fecha_descuento_desde: e.target.value})} data-testid="form-fecha-desde-amb" />
                  </div>
                  <div className="space-y-2">
                    <Label>Fecha de Descuento - Hasta</Label>
                    <Input type="date" value={formData.fecha_descuento_hasta || ""} onChange={(e) => setFormData({...formData, fecha_descuento_hasta: e.target.value})} data-testid="form-fecha-hasta-amb" />
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} className="bg-orange-600 hover:bg-orange-700" data-testid="btn-guardar-amb">{editingItem ? "Actualizar" : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Confirmar Eliminacion</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Esta seguro de eliminar este clasificador?</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
            <Button variant="destructive" onClick={confirmDelete} data-testid="btn-confirm-delete-amb">Eliminar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClasificadorAmbulantes;
